import * as Sentry from '@sentry/vue';
import { debounce, noop } from 'lodash';
import Pusher, { Channel } from 'pusher-js';
import { PusherConfig } from '@/shared/constants/PusherConfig';
import ConfigService from '@/shared/services/ConfigService';
import { present } from '@/shared/utils/typescript';

/**
 * Delay before disconnecting when there are no more active event bindings, in milliseconds.
 */
const DISCONNECT_DEBOUNCE_MS = 60 * 1000;

/**
 * Timeout after which to stop waiting, thereby rejecting the returned promise, in milliseconds.
 */
const SEQUENCE_TIMEOUT_MS = 60 * 1000;

type Event = any;
/**
 * A function that handles an incoming push event
 */
type EventHandler = (event?: Event) => void;

/**
 * A function that handles a sequence of incoming push events
 * @param resolve should be called to complete the sequence successfully, similar to {@link Promise#resolve}
 * @param reject can be called to fail the sequence, similar to {@link Promise#reject}
 */
type EventSequenceHandler = (event: Event, resolve: EventHandler, reject: (reason?: any) => void) => void;

/**
 * Default {@link EventSequenceHandler} which terminates the sequence with the first event received.
 */
function resolveFirst(event: Event, resolve: EventHandler): void {
  resolve(event);
}

/**
 * A function that should be called to release (i.e. unbind) an event binding
 */
export type Unbinder = () => void;

/**
 * Allows for receiving push events from the server.
 * This is currently implemented using Pusher.
 * A connection is opened only when needed, i.e. while specific events are actively bound to.
 */
class PushService {
  private pusher: Pusher | null = null;
  private subscriptionMap = new Map<string, Subscription>();

  /**
   * Register a function to be called when events of a given name occur.
   * To keep this implementation agnostic, a single event name is used.
   * In the case of the current Pusher implementation, this should be in the format "<channel name>/<event name>".
   */
  bind(name: string, handler: EventHandler): Unbinder {
    const [channelName, eventName, ...rest] = name.split('/');
    if (rest.length > 0) {
      // eslint-disable-next-line no-console
      console.error(`Invalid event name "${name}"!`);
      Sentry.captureMessage(`Invalid event name: ${name}`, {
        level: 'error',
        tags: { section: 'pusher', method: 'bind' },
        extra: { name },
      });
      return noop;
    }

    let subscription = this.subscriptionMap.get(channelName);
    if (!subscription) {
      const pusher = this.connect();
      subscription = new Subscription(pusher.subscribe(channelName));
      this.subscriptionMap.set(channelName, subscription);
    }

    const binding = subscription.bind(eventName, handler);

    return () => this.doUnbind(channelName, eventName, handler, binding);
  }

  /**
   * Convenience method to call (multiple) {@link #Unbinder} functions as returned by {@link #bind}.
   */
  // eslint-disable-next-line class-methods-use-this
  unbind(unbinders: Unbinder | Array<Unbinder>): void {
    if (Array.isArray(unbinders)) {
      unbinders.forEach(b => b());
    } else if (typeof unbinders === 'function') {
      unbinders();
    } else {
      // eslint-disable-next-line no-console
      console.warn('unbind called with something other than function(s)');
      Sentry.captureMessage('unbind called with something other than function(s)', {
        level: 'warning',
        tags: { section: 'pusher', method: 'unbind' },
        extra: { unbinders },
      });
    }
  }

  /**
   * Convenience method for handling a finite sequence of events.
   * Binds and then automatically unbinds once the sequence completes.
   * Each event received is passed to a handler function that decides when to stop
   * the sequence by calling one of the passed in resolve or reject functions.
   *
   * @see #bind
   *
   * @returns a promise that resolves when the sequence is complete.
   */
  sequence(name: string, handler: EventSequenceHandler = resolveFirst, timeout = SEQUENCE_TIMEOUT_MS): Promise<Event> {
    let unbind: Unbinder;
    let tid: ReturnType<typeof setTimeout>;
    return new Promise((resolve, reject) => {
      unbind = this.bind(name, event => handler(event, resolve, reject));
      // eslint-disable-next-line prefer-promise-reject-errors
      tid = setTimeout(() => reject('timeout'), timeout);
    }).finally(() => {
      unbind();
      clearTimeout(tid);
    });
  }

  private doUnbind(channelName: string, eventName: string, handler: EventHandler, binding: symbol): void {
    if (!this.pusher) {
      // eslint-disable-next-line no-console
      console.warn('not connected');
      Sentry.captureMessage('Pusher not connected in doUnbind', {
        level: 'warning',
        tags: { section: 'pusher', method: 'doUnbind' },
        extra: { channelName, eventName },
      });
      return;
    }

    const subscription = this.subscriptionMap.get(channelName);
    if (!subscription) {
      // eslint-disable-next-line no-console
      console.warn(`no subscription for "${channelName} with event "${eventName}"`);
      Sentry.captureMessage(`no subscription for "${channelName} with event "${eventName}"`, {
        level: 'warning',
        tags: { section: 'pusher', method: 'doUnbind' },
        extra: { channelName, eventName },
      });
      return;
    }

    subscription.unbind(eventName, handler, binding);

    if (subscription.bindings.size === 0) {
      this.pusher.unsubscribe(channelName);
      this.subscriptionMap.delete(channelName);
      this.disconnectDebounced();
    }
  }

  private connect(): Pusher {
    if (!this.pusher) {
      if (!ConfigService.get('PUSHER_APP_KEY')) {
        // eslint-disable-next-line no-console
        Sentry.captureMessage('Pusher not configured', {
          level: 'warning',
          tags: { section: 'pusher', method: 'connect' },
        });
      }

      this.pusher = new Pusher(present(ConfigService.get('PUSHER_APP_KEY')), {
        cluster: PusherConfig.PUSHER_CLUSTER,
      });

      this.pusher.connection.bind('error', (error: any) => {
        Sentry.captureException(error, {
          level: 'error',
          tags: { section: 'pusher', method: 'connect' },
          extra: { error },
        });
      });
    }
    return this.pusher;
  }

  private disconnect(): void {
    if (this.pusher && this.subscriptionMap.size === 0) {
      this.pusher.disconnect();
      this.pusher = null;
    }
  }

  private disconnectDebounced = debounce(this.disconnect.bind(this), DISCONNECT_DEBOUNCE_MS);
}

/**
 * Internal supporting class.
 */
class Subscription {
  channel: Channel;
  bindings = new Set<symbol>();

  constructor(channel: Channel) {
    this.channel = channel;
  }

  bind(eventName: string, handler: EventHandler): symbol {
    this.channel.bind(eventName, handler);
    const binding = Symbol('binding');
    this.bindings.add(binding);
    return binding;
  }

  unbind(eventName: string, handler: EventHandler, binding: symbol): void {
    if (this.bindings.has(binding)) {
      this.channel.unbind(eventName, handler);
      this.bindings.delete(binding);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Unknown event binding for "${eventName}"! Maybe already unbound?`);
      Sentry.captureMessage(`Unknown event binding for: ${eventName}`, {
        level: 'warning',
        tags: { section: 'pusher', method: 'Subscription.unbind' },
        extra: { eventName, handler, binding },
      });
    }
  }
}

const pushService = new PushService();
export default pushService;
